(ns dashboard.components.search.search-box
  (:require [clojure.string :as str]
            [dashboard.components.answer-options :refer [AnswerOptions]]
            [dashboard.components.copy :refer [CopyToClipboard]]
            [dashboard.components.keybindings :refer [keybindings]]
            [dashboard.components.search.events :as events]
            [dashboard.components.search.subs :as subs]
            [dashboard.html :as html]
            [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]
            [dashboard.ui :as ui]
            [dashboard.utils :as utils]
            [dashboard.views.questionnaire.question-lab.common :as lab-common]
            [io.repliance.answer-data :as answer-data]
            [re-frame.core :as rf]
            [react]
            [reitit.frontend.easy :as rfe]))

(def default-search-over "answer-bank")

(rf/reg-event-db
 ::set-result-index
 (fn [db [_ i]]
   (assoc-in db [:search :index] i)))

(rf/reg-event-fx
 ::do-search
 (fn [{:keys [db]} [_ query request-id on-search-complete]]
   (let [search-over (get-in db [:search :search-over] default-search-over)]
     (if-not (str/blank? query)
       {:http-xhrio {:method :get
                     :uri "/api/search"
                     :format http-config/transit-request-format
                     :response-format http-config/transit-response-format
                     :url-params {:query query
                                  :search-over search-over}
                     :on-success [::set-search-result request-id on-search-complete]
                     :on-failure [::on-search-error on-search-complete]}}
       (do
         (on-search-complete)
         {:db (assoc-in db [:search :results] [])})))))

(rf/reg-event-fx
  ::on-search-error
  (fn [_ [_ on-search-complete error]]
    (on-search-complete)
    {:dispatch [::ui/set-error error]}))

(rf/reg-event-fx
 ::set-search-result
 (fn [{:keys [db]} [_ request-id on-search-complete {:keys [status message results]}]]
   (on-search-complete)
   {:db (update db :search
                (fn [search]
                  (let [old-results (:results search)]
                    (assoc search :results
                           (if (= "ok" status)
                             (if (= (:last-request-id search) request-id)
                               ;; Keep new results only if they were from the latest request
                               results
                               (do
                                 (println "Ignoring search results that aren't from last request")
                                 old-results))
                             ;; Keep old results if search failed
                             old-results)))))
    :fx (case status
          "fail" [[:dispatch [::ui/set-error {:message message}]]]
          "pending" [[:dispatch [::notifications/add {:message message}]]]
          [])}))

(def throttle-duration-ms 740)

(rf/reg-event-fx
 ::dispatch-search
 (fn [{:keys [db]} [_ value on-search-complete]]
   (let [;; To allow discarding results that are not from the latest request
         last-request-id (random-uuid)]
     {:db (update db :search (fn [search]
                               (assoc search
                                      :value value
                                      :last-request-id last-request-id)))
      :fx [[:dispatch [::do-search value last-request-id on-search-complete]]]})))

(rf/reg-event-fx
 ::set-search-over
 (fn [{:keys [db]} [_ search-over on-search-complete]]
   (let [value (get-in db [:search :value])
         last-request-id (random-uuid)]
     {:db (update db
                  :search
                  assoc
                  :search-over search-over
                  :last-request-id last-request-id)
      ;; Trigger new search on type change
      :dispatch [::do-search value last-request-id on-search-complete]})))

(rf/reg-sub
 ::get-search-query
 (fn [db _]
   (get-in db [:search :value] "")))

(defn no-results []
  [:div.content.p-2
   [:b "No results found"]
   [:p "Here are a few tips to help:"]
   [:ol
    [:li "Check spelling"]
    [:li "Use less specific search terms (you can always narrow your results)"]]])

(defn response-element [response]
  [:<>
   [:b.response.px-2.py-0
    {:class (case (some-> response str/lower-case str/trim)
              "no" ["has-text-white" "has-background-danger"]
              "yes" ["has-text-white" "has-background-success"]
              "n/a" ["has-background-warning"]
              ["has-background-grey-lighter"])}
    (html/as-hiccup-with-anchors response)]
   [CopyToClipboard "Response" response]])

(defn highlighted-text [text]
  [:div
   {:style {:height "100%"}
    :dangerouslySetInnerHTML
    {:__html text}}])

(defn render-questionnaire-element
  [{:keys [questionnaire-id questionnaire-name questionnaire-filename]}]
  [:<>
   (when questionnaire-id
     [:div
      [:span.ml-2 "Questionnaire: "]
      [:a {:href (rfe/href :route.view/questionnaire-question-list
                           {:questionnaire-id questionnaire-id})}
       (or questionnaire-name questionnaire-filename)]])])

(defn render-analysys-element
  [{:keys [analysis-highlight]}]
  [:<>
   (when analysis-highlight
     [:div
      [:span.ml-2 "Analysis: "]
      (html/as-hiccup-with-anchors analysis-highlight)])])

(defn render-inferred-document-element
  [fetch-file-preview-url {:keys [source answer-data document-id]}]
  [:<>
   (when (= "inferred_document" source)
     [:div
      [:div
       [:span.ml-2 "Response: "]
       [response-element (answer-data/document->response answer-data)]]
      [:div
       [:span.ml-2 "Comment: "]
       (answer-data/document->comment answer-data)]
      [:div
       [:span.ml-2 "Document: "]
       (let [filename (-> answer-data :meta :name)]
         [:a {:href "#"
              :on-click (fn [e]
                          (.stopPropagation e)
                          (fetch-file-preview-url filename
                                                  document-id))}
          filename])]
      [:div.ml-2
       [:div.columns.m-0.p-0
        [:div.column.is-narrow.p-0 "Context: "]
        [:div.column.p-0
         [:div.ml-2 [lab-common/render-document-answer answer-data]]]]]])])

(defn render-comment-element
  [{:keys [comment comment-highlight]}]
  [:<>
   (when-let [comment-element (cond
                                comment-highlight
                                [:span.has-text-gray-light
                                 (html/as-hiccup-with-anchors comment-highlight)]

                                (and comment (not (str/blank? comment)))
                                [:span.has-text-gray-light comment])]
     [:div
      [:span.ml-2 "Comment: "]
      comment-element
      [CopyToClipboard "Comment" comment]])])

(defn render-result
  [fetch-file-preview-url
   do-not-close-on-click
   selected-document-id
   index
   i
   {:keys [question-id
           question
           response
           ;comment
           question-highlight
           response-highlight
           ;comment-highlight
           result-type
           source
           content
           ;highlight_start
           ;highlight_end
           ;analysis-highlight
           ;answer-data
           document-id
           filename
           matched-content
           ;questionnaire-id
           ;questionnaire-name
           ;questionnaire-filename
           options
           results]
    document-meta :meta
    :as result-set}]
  [:div.p-2
   {:key (cond
           (= "Document" source)
           content

           (= result-type "document")
           document-id

           :else question-id)
    :class (if (and (>= index 0) (= index i))
             "is-active-result"
             (cond
               (odd? i) "has-background-light"
               (even? i) "has-background-white"))
    :on-click #(rf/dispatch [::set-result-index i])}

   (cond
     (= result-type "document")
     [:div.my-0
      [:div
       [:b.is-size-5 filename]
       " ("
       (count results)
       " match"
       (if (> (count results) 1)
         "es)"
         ")")]
      (html/as-hiccup-with-anchors matched-content)
      (some->> results
               (sort-by :page)
               (take (if selected-document-id 25 3))
               (mapv
                (fn [match]
                  [:div
                   [:a
                    {:href "#"
                     :on-click (fn [e]
                                 (.stopPropagation e)
                                 (fetch-file-preview-url filename
                                                         document-id
                                                         match
                                                         results)
                                 (when-not do-not-close-on-click
                                  (rf/dispatch [::events/set-search-visible false])))}
                    [:div
                     [:b.mr-4.is-size-7 "Page " (:page match)]
                     (:name document-meta)]
                    (highlighted-text (:matched_content match))]
                   [CopyToClipboard "Document content" (:matched_content match)]]))
               (into [:div.my-0]))
      [CopyToClipboard "Document content" matched-content]]
     :else
     [:div.my-0
      [:div
       [:div>span.has-text-weight-semibold
        (if question-highlight
          (html/as-hiccup-with-anchors question-highlight)
          question)]
       (when-let [options (seq options)]
         [AnswerOptions {:options options}])
       (when (or response response-highlight)
         [:div
          [:span.ml-2 "Response: "]
          [response-element (or response response-highlight)]])]])

   [render-comment-element result-set]
   [render-inferred-document-element fetch-file-preview-url result-set]
   [render-analysys-element [render-comment-element result-set]]
   [render-questionnaire-element result-set]])

(defn search-panel
  [fetch-file-preview-url do-not-close-on-click selected-document-id only-documents]
  (let [dispatch-search (utils/debounce
                         (fn [search-query set-is-searching]
                           (rf/dispatch [::dispatch-search search-query #(set-is-searching false)]))
                         throttle-duration-ms)]
    (fn []
      (let [global-query @(rf/subscribe [::get-search-query])
            [search-input set-search-input] (react/useState global-query)
            [is-searching set-is-searching] (react/useState nil)
            [ref set-ref] (react/useState nil)
            {:keys [value results index search-over]
             :or {search-over default-search-over}} @(rf/subscribe [::subs/search])
            search-over (if (or selected-document-id
                                only-documents)
                          "documents"
                          search-over)
            select-search-input (react/useCallback (fn [el]
                                                     (when el
                                                       (.select el)))
                                                   #js [])]
        ;; Close quick search if clicking outside the panel
        (react/useEffect
         (fn []
           (let [handle-click (fn [e]
                                (when (and ref
                                           (not (.contains ref (.-target e))))
                                  (rf/dispatch [::events/set-search-visible false])))]
             (.addEventListener js/document "mousedown" handle-click)
              ;; Unmount
             #(.removeEventListener js/document "mousedown" handle-click)))
         #js [])

        (react/useEffect
         (fn []
           (set-search-input global-query))
         #js [global-query])

        (react/useEffect
         (fn []
           (when (or selected-document-id only-documents)
             (rf/dispatch [::set-search-over "documents" #(set-is-searching false)])))
         #js [selected-document-id])

        [:f> keybindings
         {:actions {"Escape" {:action #(rf/dispatch [::events/set-search-visible false])
                              :opts {:allow-outside-body true}}}}
         [:div.panel.is-link.quick-search.is-clipped.mx-2.has-background-white
          {:ref set-ref}
          [:div.panel-heading.p-1
           [:div.field>div.control.has-icons-left
            [:span.icon.is-left>i.fas.fa-magnifying-glass]
            [:input.input
             {:id events/search-element-id
              :type "search"
              :placeholder (if (= search-over "llm-answer-bank")
                             "Enter your search here. Try pasting an entire question!"
                             "Enter your search here")
              :auto-focus true
              :auto-complete "repliance-quicksearch"
              :value search-input
              :on-change (fn [e]
                           (let [search-query (.. e -target -value)]
                             (set-search-input search-query)
                             (set-is-searching true)
                             (dispatch-search search-query set-is-searching)))
              :ref select-search-input}]]]

          ; SEARCH RESULTS :
          (when (seq results)
            [:div.p-0.has-background-white.search-results
             (cond->> results
               selected-document-id
               (filter (comp (partial = selected-document-id) :document-id))
               true
               (map-indexed
                (partial render-result
                         fetch-file-preview-url
                         do-not-close-on-click
                         selected-document-id
                         index))
               true
               (into [:<>]))])

          (when (and (not (str/blank? value))
                     (not is-searching)
                     (empty? results))
            [no-results])
          [:div.block.level.shortcuts.p-2
           [:div.level-left
            (when (and results
                       (not is-searching)
                       (not (str/blank? value)))
              [:span (count results) " results"])
            (when is-searching
              [:div
               [:span.icon>i.fas.fa-magnifying-glass]
               "Searching..."])]
           (when-not (or selected-document-id
                         only-documents)
             [:div.level-right
              [:div.level-item
               [:span.mr-2
                "Search over:"]
               [:div.field
                [:div.control
                 [:label.radio {:for "answer-bank"}
                  [:input {:type "radio"
                           :id "answer-bank"
                           :checked (= search-over "answer-bank")
                           :name "search-over"
                           :on-change (fn [_]
                                        (set-is-searching true)
                                        (rf/dispatch [::set-search-over "answer-bank" #(set-is-searching false)]))}]
                  " Answer bank "]
                 [:label.radio {:for "llm-answer-bank"}
                  [:input {:type "radio"
                           :id "llm-answer-bank"
                           :checked (= search-over "llm-answer-bank")
                           :name "search-over"
                           :on-change (fn [_]
                                        (set-is-searching true)
                                        (rf/dispatch [::set-search-over "llm-answer-bank" #(set-is-searching false)]))}]
                  " Answer bank (LLM ✨) "]
                 [:label.radio {:for "documents"}
                  [:input {:type "radio"
                           :id "documents"
                           :checked (= search-over "documents")
                           :name "search-over"
                           :on-change (fn [_]
                                        (set-is-searching true)
                                        (rf/dispatch [::set-search-over "documents" #(set-is-searching false)]))}]
                  " Documents "]]]]])]]]))))
